import { AdminMatches } from '@platform/client';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { useState, useContext } from 'react';
import { AdminTableContext } from '../../AdminTable';
import { useQueryClient } from '@tanstack/react-query';
import {
  MatchProgressEnum,
  MatchDecisionEnum,
  MatchDeclineReasonEnum,
} from '@platform/client/schemas';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import {
  FormsInputDropdown,
  InlineFormInput,
  FormsCheckBox,
  DatePicker,
} from '@platform/components';

type UpdateProgressFormData = {
  newProgress: MatchProgressEnum;
  outcome: MatchDecisionEnum | null;
  declineUserType: 'representative' | 'professional';
  declineReason: MatchDeclineReasonEnum | null;
  declineReasonText: string | null;
  sendEmail: boolean | null;
  scheduleEmailDate: Date | null;
};

/** AdminFormModal allowing internal users to manually update a matches progress, along with decision and supporting reason */
export const UpdateProgressModal = ({
  isTeam = false,
  matchGroupId,
  onClose,
}: {
  isTeam?: boolean;
  matchGroupId: number;
  onClose: () => void;
}) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { ...functions } = useForm<UpdateProgressFormData>();
  const queryClient = useQueryClient();
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const progressChangeMutation = AdminMatches.useAdminEditMatchProgress({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onSuccess: () => {
        setModalStage('success');
        queryClient.invalidateQueries({
          queryKey: [...AdminMatches.getGetAdminMatchByIdQueryKey(matchGroupId)],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminMatches.getGetAdminMatchesQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
      },
      onError: () => {
        setModalStage('error');
      },
    },
  });
  const onSubmit: SubmitHandler<UpdateProgressFormData> = (data) => {
    progressChangeMutation.mutate({
      matchGroupId,
      data: {
        newProgress: data.newProgress,
        decision:
          data.outcome ?
            {
              decision: data.outcome,
              decisionUserType: data.declineUserType,
              declineReason: data.declineReason ?? undefined,
              declineReasonText: data.declineReasonText ?? undefined,
            }
          : undefined,
        sendEmail: data.sendEmail ?? false,
        scheduledSendDate: data.scheduleEmailDate?.toISOString() ?? undefined,
      },
    });
  };
  return (
    <AdminFormModal
      title='Update Match Progress'
      modalStage={modalStage}
      onClose={onClose}
      submitCTA={{
        callback: () => functions.handleSubmit(onSubmit)(),
        text: 'Save update',
      }}
    >
      <UpdateProgressForm formFunctions={functions} isTeam={isTeam} />
    </AdminFormModal>
  );
};

/** Remove accepted, this is done by selecting next progress */
const outcomeOptions = Object.values(MatchDecisionEnum)
  .filter((decision) => decision !== 'accepted')
  .map((decision) => {
    return {
      text: decision,
      textValue: decision,
    };
  });

/** Update match progress form. This is intentionally not a <form> as the <Modal> component has this baked in */
const UpdateProgressForm = ({
  formFunctions,
  isTeam = false,
}: {
  formFunctions: ReturnType<typeof useForm<UpdateProgressFormData>>;
  isTeam?: boolean;
}) => {
  const { register, watch, control, formState } = formFunctions;
  const selectedProgress = watch('newProgress');
  const selectedOutcome = watch('outcome');
  const sendEmail = watch('sendEmail');

  // wipe decisions so we don't retain outdated selections
  const resetOutcomeFields = () => {
    formFunctions.setValue('outcome', null);
    formFunctions.setValue('declineReason', null);
    formFunctions.setValue('declineReasonText', null);
  };
  if (selectedProgress !== 'discovery' && selectedProgress !== 'introduction') {
    resetOutcomeFields();
  }
  const progressOptions = Object.values(MatchProgressEnum)
    .filter((progress) => !isTeam || progress !== 'introduction')
    .map((progress) => {
      return {
        text: progress,
        textValue: progress,
      };
    });

  return (
    <div className='flex flex-col gap-2'>
      <FormsInputDropdown
        label='New Progress'
        type='default'
        control={control}
        name='newProgress'
        items={progressOptions}
        onSelectionChange={() => resetOutcomeFields()}
        rules={{ required: 'Progress is required' }}
        error={formState.errors.newProgress}
        inputDisabled
      />
      {(selectedProgress === 'discovery' || selectedProgress === 'introduction') && (
        <FormsInputDropdown
          label='Outcome'
          type='default'
          control={control}
          name='outcome'
          items={outcomeOptions}
          rules={{ required: 'Outcome is required' }}
          error={formState.errors.outcome}
          inputDisabled
        />
      )}
      {selectedProgress === 'discovery' && selectedOutcome && selectedOutcome !== 'pending' && (
        <FormsInputDropdown
          label='Decision User Type'
          type='default'
          control={control}
          name='declineUserType'
          rules={{ required: 'Decision User Type is required' }}
          items={[
            { text: 'professional', textValue: 'professional' },
            { text: 'representative', textValue: 'representative' },
          ]}
          error={formState.errors.declineUserType}
          inputDisabled
        />
      )}
      {selectedOutcome === 'declined' && (
        <FormsInputDropdown
          label='Decline Reason'
          type='default'
          control={control}
          name='declineReason'
          items={Object.values(MatchDeclineReasonEnum).map((reason) => {
            return {
              text: reason,
              textValue: reason,
            };
          })}
          rules={{ required: 'Decline Reason is required' }}
          error={formState.errors.declineReason}
          inputDisabled
        />
      )}
      {selectedOutcome === 'declined' && watch('declineReason') === 'other' && (
        <InlineFormInput
          label='Other Decline Reason'
          register={() => register('declineReasonText', { required: 'Other reason is required' })}
          errors={formState.errors.declineReasonText}
        />
      )}
      {getSendEmailDescription(selectedProgress, selectedOutcome, isTeam) && (
        <FormsCheckBox
          size='sm'
          description={getSendEmailDescription(selectedProgress, selectedOutcome, isTeam)}
          control={control}
          name='sendEmail'
        />
      )}
      {sendEmail && (
        <Controller
          control={control}
          name='scheduleEmailDate'
          render={({ field }) => (
            <DatePicker
              label='Scheduled Send Date (leave blank to send immediately)'
              onChange={(date) => field.onChange(date as Date)}
            />
          )}
        />
      )}
    </div>
  );
};

/** Provides dynamic description to the send email checkbox so user knows which email is being sent */
export const getSendEmailDescription = (
  progress?: MatchProgressEnum,
  outcome?: MatchDecisionEnum | null,
  isTeam?: boolean,
) => {
  if (
    (progress === MatchProgressEnum.introduction || progress === MatchProgressEnum.discovery) &&
    !outcome
  ) {
    return;
  }
  switch (progress) {
    case MatchProgressEnum.introduction:
      if (outcome !== MatchDecisionEnum.pending) {
        return 'Send generic update email';
      }
      return 'Send new introduction email';
    case MatchProgressEnum.discovery:
      if (outcome !== MatchDecisionEnum.pending) {
        return 'Send generic update email';
      }
      if (isTeam) {
        return 'Send propose team match email to rep and schedule reminders';
      }
      return 'Send introduction accepted email & schedule discovery reminders';
    case MatchProgressEnum.planning:
      return 'Send project matched email and schedule planning reminders';
    case MatchProgressEnum.action:
      return 'Schedule action reminders';
    case MatchProgressEnum.reflect:
      return 'Schedule survey reminders';
    default:
      return;
  }
};
